import * as React from 'react';

import { LOCALE } from 'features/ui/enums';
import { getCurrentLocale } from 'features/services/utils/env-helper';
import { DisposalForm, ResidenceType } from 'features/search/filters/enums';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import { getConstructionFormName, getDisposalFormName } from 'features/services/utils/name-helpers';

import './style.scss';

import { Facts } from '.';
import { QuickFactsItem } from './item';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';

interface Props {
    showMore?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by page type
    viewModel: any;

    addressString: string;
    areaString: string;
    energyMarking: string;
    roomString: string;
}

export const ReferenceQuickFacts: React.FunctionComponent<Props> = ({ 
    showMore, viewModel, 
    addressString, areaString, energyMarking, roomString 
}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const numberOfVisibleItems = isMobile ? 5 : 20;
    const facts : Facts[] = [
        {key: 'address', devCounter: '01', //(26) 
            label: getFormattedMessage('jm.components.QuickFacts.address'), 
            fact: addressString, 
            condition: addressString 
        },
        {key: 'architect', devCounter: '02', //(25) 
            label: getFormattedMessage('jm.components.QuickFacts.architect'), 
            fact: viewModel.architect, 
            condition: viewModel.architect 
        },
        {key: 'completedYear', devCounter: '03',//(24)  
            label: getFormattedMessage('jm.components.QuickFacts.completed'), 
            fact: viewModel.completedYear, 
            condition: viewModel.completedYear 
        },
        {key: 'constructionForm', devCounter: '04',//(21)  
            label: getFormattedMessage('jm.components.QuickFacts.constructionForm'), 
            fact: getConstructionFormName(viewModel.constructionForm as ResidenceType), 
            condition: viewModel.constructionForm 
        },
        {key: 'disposalForm', devCounter: '05',//(22)  
            label: getFormattedMessage('jm.components.QuickFacts.disposalForm'), 
            fact: getDisposalFormName(viewModel.disposalForm as DisposalForm), 
            condition: viewModel.disposalForm 
        },
        {key: 'energyMarking', devCounter: '06',//(19)  
            label: getFormattedMessage('jm.components.QuickFacts.energyMarking'), 
            fact: energyMarking, 
            condition: energyMarking && (!viewModel.swanLabel || getCurrentLocale() === LOCALE.NO)
        },
        {key: 'minMaxArea', devCounter: '07',//(11)  
            label: getCurrentLocale() === LOCALE.NO
                ? getFormattedMessage('jm.components.QuickFacts.noBRAi')
                : getFormattedMessage('jm.components.QuickFacts.area'), 
            fact: areaString, 
            condition: areaString 
        },
        {key: 'minMaxRooms', devCounter: '08',//(06)  
            label: getFormattedMessage('jm.components.QuickFacts.rooms'), 
            fact: roomString, 
            condition: roomString
        },
        {key: 'numberOfObjects', devCounter: '09',//(18)  
            label: getFormattedMessage('jm.components.QuickFacts.numberOfObjects'), 
            fact: viewModel.nrOfObjects, 
            condition: (viewModel.nrOfObjects !== 0 && viewModel.nrOfObjects !== null && typeof viewModel.nrOfObjects !== 'undefined') 
        },
        {key: 'swanLabel', devCounter: '10',//(20)  
            label: getFormattedMessage('jm.components.QuickFacts.swanLabel'), 
            fact: getFormattedMessage('jm.components.QuickFacts.yes'), 
            condition: viewModel.swanLabel 
        }
    ];

    const renderFactsByOrder = (factOrder: string[]) => {
        let factIndex = 0;
        return factOrder.map((key, index) => {
            const fact = facts.find(f => f.key === key);
            if (!fact || !fact.condition) return null;
            factIndex++;

            return (
                <QuickFactsItem
                    key={fact.key}
                    index={index.toString()}
                    devCounter={fact.devCounter}
                    itemLabel={fact.label}
                    itemFact={fact.fact}
                    isHidden={!showMore && factIndex > numberOfVisibleItems} 
                />
            );
        })
        .filter(Boolean);
    };

    const order = [
        'constructionForm',
        'disposalForm',
        'address',
        'completedYear',
        'numberOfObjects',
        'energyMarking',
        'swanLabel',
        'minMaxRooms',
        'minMaxArea',
        'architect'
    ];

    return (
        <dl className="quick-facts__list">
            {renderFactsByOrder(order)}
        </dl>
    )
};